import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db, doc, updateDoc, getDoc } from '../../Services/firebase-config';

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const number = parseFloat(value);
  if (isNaN(number)) return value;
  return number.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const BasisCosting = () => {
  const navigate = useNavigate();
  const { kibaloId } = useParams();
  const cleanKibaloId = kibaloId ? kibaloId.split('/')[0] : kibaloId;

  const [user, setUser] = useState(null);
  const [kibaloName, setKibaloName] = useState('');
  const [productName, setProductName] = useState('');
  const selectedProduct = localStorage.getItem('selectedProduct');

  const [costingData, setCostingData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    costBasis: 'year',
    unit: '',
    quantity: '',
    productionPerAcre: ''
  });

  // Authentication
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) setUser(authUser);
      else navigate('/login');
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch kibalo data
  useEffect(() => {
    const fetchKibaloData = async () => {
      if (!user || !cleanKibaloId) return;
      const kibaloRef = doc(db, 'users', user.uid, 'kibalos', cleanKibaloId);
      const kibaloSnap = await getDoc(kibaloRef);
      if (kibaloSnap.exists()) {
        const data = kibaloSnap.data();
        setKibaloName(data.name || '');
        setProductName(data.productName || selectedProduct || '');
        setCostingData(data.costingBasis || null);
      }
    };
    fetchKibaloData();
  }, [user, cleanKibaloId, selectedProduct]);

  const handleNext = () => navigate(`/establishment-costing/${cleanKibaloId}`);
  const handlePrevious = () => navigate(`/calculator/${cleanKibaloId}`);

  const handleOpenModal = () => {
    if (costingData) setFormData(costingData);
    else setFormData({ costBasis: 'year', unit: '', quantity: '', productionPerAcre: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSave = async () => {
    setCostingData(formData);
    if (user && cleanKibaloId) {
      const kibaloRef = doc(db, 'users', user.uid, 'kibalos', cleanKibaloId);
      try { await updateDoc(kibaloRef, { costingBasis: formData }); }
      catch (error) { console.error('Error updating costing basis:', error); }
    }
    handleCloseModal();
  };

  const handleDelete = async () => {
    setCostingData(null);
    if (user && cleanKibaloId) {
      const kibaloRef = doc(db, 'users', user.uid, 'kibalos', cleanKibaloId);
      try { await updateDoc(kibaloRef, { costingBasis: null }); }
      catch (error) { console.error('Error deleting costing basis:', error); }
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6">

        {/* Header */}
        <h1 className="text-2xl font-bold text-gray-800">
          Basis of Costing for {kibaloName}  
        </h1>
        <p className="text-gray-600 mb-4">Manage the basis of costing for your product.</p>

        {/* Add button */}
        {!costingData && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleOpenModal}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow text-sm font-medium"
            >
              Add Costing Basis
            </button>
          </div>
        )}

        {/* Costing Table */}
        {costingData ? (
          <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold">
                {['Basis', 'Unit', 'Quantity', 'Production/Acre', 'Actions'].map(title => (
                  <th key={title} className="border px-4 py-3 text-left">{title}</th>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="border px-4 py-3">{costingData.costBasis}</td>
                  <td className="border px-4 py-3">{costingData.unit}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(costingData.quantity)}</td>
                  <td className="border px-4 py-3">{costingData.costBasis === 'acreage' ? formatNumberWithCommas(costingData.productionPerAcre) : 'N/A'}</td>
                  <td className="border px-4 py-3 flex gap-2">
                    <button onClick={handleOpenModal} className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md text-xs">
                      Edit
                    </button>
                    <button onClick={handleDelete} className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs">
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No costing basis added yet. Click "Add Costing Basis" to get started.</div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
            <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
              <h2 className="text-xl font-semibold mb-4">{costingData ? 'Edit' : 'Add'} Costing Basis</h2>

              {/* Radio options */}
              <div className="mb-4 flex gap-6">
                {['year', 'acreage'].map(value => (
                  <label key={value} className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      value={value}
                      checked={formData.costBasis === value}
                      onChange={(e) => setFormData({ ...formData, costBasis: e.target.value, productionPerAcre: value === 'year' ? '' : formData.productionPerAcre })}
                      className="form-radio h-5 w-5 text-blue-500"
                    />
                    <span className="text-gray-700">{value === 'year' ? 'Production per year' : 'Production per acreage'}</span>
                  </label>
                ))}
              </div>

              {/* Form fields */}
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-700 text-sm font-medium">Unit of Measure</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium">Quantity</label>
                  <input
                    type="text"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder={formData.costBasis === 'year' ? 'Production per year' : 'Number of acres'}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {formData.costBasis === 'acreage' && (
                  <div>
                    <label className="block text-gray-700 text-sm font-medium">Production per Acre/Year</label>
                    <input
                      type="number"
                      value={formData.productionPerAcre}
                      onChange={(e) => setFormData({ ...formData, productionPerAcre: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Modal buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={handleCloseModal} className="px-4 py-2 bg-gray-500 hover:bg-gray-700 text-white rounded-lg">Cancel</button>
                <button onClick={handleSave} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button onClick={handlePrevious} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg">Back to Table of Contents</button>
          <button onClick={handleNext} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Go to Establishment</button>
        </div>
      </div>
    </div>
  );
};

export default BasisCosting;
